import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Lock, X, Eye, EyeOff } from 'lucide-react';

const PasswordResetModal = ({ onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setMessage('Your password has been updated.');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err.message || 'Could not update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-root fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="glass-card rounded-2xl border border-[#3B82F6]/30 p-8 w-full max-w-md relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#64748B] hover:text-[#1E293B] transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <h2 className="serif text-3xl font-bold text-[#1E293B] mb-2">Set new password</h2>
          <p className="text-sm text-[#475569]">Choose a new password for your account.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}
          {message && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl text-green-600 text-sm">
              {message}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-[#475569] mb-2">New password</label>
            <div className="relative isolate">
              <Lock
                className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-[#64748B]"
                aria-hidden
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                autoComplete="new-password"
                className="relative z-0 w-full rounded-xl border border-[#3B82F6]/30 bg-[#E2E8F0]/30 py-3 pl-10 pr-12 text-[#1E293B] placeholder:text-[#64748B] glass-card focus:border-[#e2c275]/50 focus:outline-none"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPassword((v) => !v);
                }}
                className="auth-password-toggle absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#64748B] hover:bg-[#E2E8F0]/60 hover:text-[#1E293B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/50"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center" aria-hidden>
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#475569] mb-2">Confirm password</label>
            <div className="relative isolate">
              <Lock
                className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-[#64748B]"
                aria-hidden
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                autoComplete="new-password"
                className="relative z-0 w-full rounded-xl border border-[#3B82F6]/30 bg-[#E2E8F0]/30 py-3 pl-10 pr-4 text-[#1E293B] placeholder:text-[#64748B] glass-card focus:border-[#e2c275]/50 focus:outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#e2c275] text-black font-semibold rounded-xl hover:bg-[#e2c275]/90 transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Saving…
              </div>
            ) : (
              'Update password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetModal;
